import { IonIcon } from '@ionic/react';
import dayjs from 'dayjs';
import { bed, business, calendar, chevronDown, card, medical, people } from 'ionicons/icons';
import React, { useState } from 'react';

import BotInfoCard from '../botInfoCard/BotInfoCard';
import './botPatientCard.scss';

interface Patients {
  patient_id: number;
  patient_name: string;
  display_name: string;
  age: string;
  mr_number: number | string;
  admission_on: string;
  bed_name: string;
  site_name: string;
  unit_name: string;
}

interface BotPatientCardProps {
  patients: Patients[];
  maxVisiblePatients?: number;
}

const BotPatientCard: React.FC<BotPatientCardProps> = ({ patients, maxVisiblePatients = 3 }) => {
  const [expandedPatientIds, setExpandedPatientIds] = useState<number[]>([]);
  const [showAllPatients, setShowAllPatients] = useState(false);

  const togglePatientDetails = (id: number) => {
    setExpandedPatientIds(prev =>
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const getPatientInitials = (name: string) => {
    if (name === '<encrypted>') return 'AB';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTimestamp = (timestamp: string) =>
    dayjs(timestamp).format('MMMM D, YYYY h:mm A');

  if (typeof patients === 'string') return <BotInfoCard message={patients} />;
  if (patients.length === 0) return null;

  const visiblePatients = showAllPatients ? patients : patients.slice(0, maxVisiblePatients);
  const remainingCount = patients.length - maxVisiblePatients;

  const getUnitColor = (unit: string) => {
    const colors: Record<string, string> = {
      Cardio: '#6e7afb',
      ICU: '#a16b0e',
      Surgery: '#1a865f',
      Oncology: '#d93a45',
      Pediatrics: '#4554fa',
      Gynecology: '#EC4899',
      Emergency: '#d93a45',
      default: '#4554fa',
    };
    return colors[unit] || colors.default;
  };

  return (
    <div className="bot-patient-card">
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <div className="header-icon">
            <IonIcon icon={people} />
          </div>
          <h3 className="header-title">Patients</h3>
        </div>
        <div className="badge">
          <span className="badge-text">{patients?.length}</span>
        </div>
      </div>

      {/* Patients List */}
      <div className="patients-list">
        {visiblePatients.map(patient => {
          const isExpanded = expandedPatientIds.includes(patient.patient_id);
          const unitColor = getUnitColor(patient.unit_name);

          return (
            <div
              key={patient.patient_id}
              className={`patient-card ${isExpanded ? 'expanded' : ''}`}
            >
              {/* Patient Header */}
              <div
                className="patient-header"
                onClick={() => togglePatientDetails(patient.patient_id)}
              >
                <div className="avatar-container">
                  <span className="avatar-text">
                    {getPatientInitials(patient.display_name)}
                  </span>
                </div>

                <div className="patient-main-info">
                  <div className="name-row">
                    <span
                      className="patient-name"
                      title={patient.display_name}
                    >
                      {patient.display_name}
                    </span>

                    {/* Unit Name Badge */}
                    {patient.unit_name && (
                      <span
                        className="unit-badge"
                        style={{ backgroundColor: unitColor }}
                      >
                        {patient.unit_name}
                      </span>
                    )}
                  </div>

                  <span className="patient-age">{patient.age}</span>
                </div>

                <IonIcon
                  icon={chevronDown}
                  className={`accordion-icon ${isExpanded ? 'rotated' : ''}`}
                />
              </div>

              {/* Patient Details */}
              {isExpanded && (
                <div className="details-grid">
                  <div className="detail-item">
                    <div className="detail-icon-container">
                      <IonIcon icon={card} style={{ color: '#4554fa' }} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">Patient ID</span>
                      <span className="detail-value">{patient.patient_id}</span>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon-container">
                      <IonIcon icon={medical} style={{ color: '#d93a45' }} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">MR Number</span>
                      <span className="detail-value">{patient.mr_number}</span>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon-container">
                      <IonIcon icon={bed} style={{ color: '#1a865f' }} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">Bed</span>
                      <span className="detail-value" title={patient.bed_name}>
                        {patient.bed_name}
                      </span>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon-container">
                      <IonIcon icon={business} style={{ color: '#a16b0e' }} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">Site</span>
                      <span className="detail-value" title={patient.site_name}>
                        {patient.site_name}
                      </span>
                    </div>
                  </div>

                  <div className="detail-item admission-info">
                    <div className="detail-icon-container">
                      <IonIcon icon={calendar} style={{ color: '#05b9bb' }} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">Admitted</span>
                      <span className="detail-value">
                        {formatTimestamp(patient.admission_on)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* +N more indicator */}
           {!showAllPatients && remainingCount > 0 && (
          <div className="show-more" onClick={() => setShowAllPatients(true)}>
            +{remainingCount} more
          </div>
        )}

        {showAllPatients && patients.length > maxVisiblePatients && (
          <div className="show-more" onClick={() => setShowAllPatients(false)}>
            Show less
          </div>
        )}
      </div>
    </div>
  );
};

export default BotPatientCard;